/**
 * Блок «Бонусы (1М+)» в карточке лида: видео-бонусы по месяцам (дата достижения).
 * Сворачиваемая таблица всех пачек блогера: пересечения 1М, оплата и отклонение.
 *
 * DESIGN SYSTEM (BLOGGER-CARD-DS 2026-08-18, spec P2). Card-exclusive child of the
 * Мониторинг tab (single importer LeadDetailsMonitoringTab.jsx), so it migrates with it:
 * DS components from './ds', layout on `oc-lead-card__*` classes in lead-card.css, the
 * three emoji (money bag / clapper / check) replaced by Phosphor Icon call sites
 * (coins / film-slate / check — iron rule 7, spec S6).
 * The table stays real <table> markup under spec decision D2 / owner ruling F8.
 * `fmtViewsCompact` now resolves through the DS barrel — the third consumer of the two
 * formatters MetricTable exports (spec S4).
 */
import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchVideoBonuses, markVideoBonusPaid, setVideoBonusRejection } from '../api/videoBonuses.js';
import { groupBonusesByMonth } from '../lib/bonusGrouping.js';
import { splitLinks, decideLinkRejection } from '../lib/videoLinkKey.mjs';
import { Badge, Button, Card, Icon, fmtViewsCompact } from './ds';

const PLATFORM_LABEL = { instagram: 'IG', tiktok: 'TT', youtube: 'YT', facebook: 'FB' };

function fmtDay (iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export default function BonusPanel ({ leadId }) {
  return <LeadBonuses key={leadId} leadId={leadId} />;
}

function LeadBonuses ({ leadId }) {
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkReason, setLinkReason] = useState('');
  const [linkResults, setLinkResults] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const query = useQuery({
    queryKey: ['video-bonuses', leadId],
    queryFn: () => fetchVideoBonuses(leadId),
    enabled: !!leadId,
    retry: (count, error) => error.status !== 403 && count < 1,
  });
  const mutation = useMutation({
    meta: { errorHandledLocally: true },
    mutationFn: ({ id, paid, rejected }) => paid === undefined ? setVideoBonusRejection(id, rejected, reason) : markVideoBonusPaid(id, paid),
    onSuccess: () => { setDecision(null); setReason(''); return queryClient.invalidateQueries({ queryKey: ['video-bonuses', leadId] }); },
  });
  const denied = query.error?.status === 403;
  const groups = groupBonusesByMonth(denied ? [] : query.data?.bonuses);
  const summary = denied ? null : query.data?.summary;
  const disabled = mutation.isPending || query.isError || query.isFetching;
  const rejectByLinks = async () => {
    const bonuses = query.data?.bonuses || [];
    const results = [];
    setLinkBusy(true);
    for (const link of splitLinks(linkText)) {
      const { row, text } = decideLinkRejection(link, bonuses);
      if (!row) { results.push({ link, text }); continue; }
      try { await setVideoBonusRejection(row.id, true, linkReason); results.push({ link, text: 'отклонён' }); } catch (error) { results.push({ link, text: message[error.message] || 'не удалось отклонить' }); }
    }
    setLinkResults(results);
    setLinkBusy(false);
    setLinkText('');
    await queryClient.invalidateQueries({ queryKey: ['video-bonuses', leadId] });
  };
  const message = { bonus_paid: 'Оплаченный бонус нельзя отклонить или восстановить.', bonus_rejected: 'Отклонённый бонус нельзя оплатить.', Forbidden: 'Нет доступа к этому действию.', bonus_write_failed: 'Не удалось изменить бонус. Повторите попытку.' };

  return (
    <Card className="oc-lead-card__bonus" data-bonus-lead={leadId}>
      <details>
      <summary className="oc-lead-card__bonus-head oc-collapsible__row" data-ds-focusable>
        <span className="oc-lead-card__bonus-headtext">
        <span className="oc-lead-card__section-title">
          <Icon name="coins" size="sm" /> Бонусы блогера · все пачки
        </span>
        <span className="oc-lead-card__bonus-summary">
          {denied ? 'Нет доступа к бонусам' : summary ? <>
            Пересечений 1М: {summary.crossings} · К оплате: {summary.unpaidEligible} · Оплачено: {summary.paid} · Отклонено: {summary.rejected}
            {query.isError && ' · Данные устарели'}
          </> : query.isLoading ? 'Загрузка бонусов…' : 'Не удалось загрузить бонусы'}
        </span>
        </span>
        <Icon name="caret-down" size="md" className="oc-collapsible__caret oc-lead-card__bonus-caret" />
      </summary>
      {!denied && query.isError && <p role="alert">{summary ? 'Данные устарели. Действия недоступны.' : 'Не удалось загрузить бонусы.'} <Button size="small" disabled={query.isFetching} onClick={() => query.refetch()}>Повторить</Button></p>}
      {!denied && mutation.isError && <p role="alert">{message[mutation.error.message] || 'Не удалось изменить бонус. Повторите попытку.'}</p>}
      {!denied && summary?.crossings === 0 && <p>Видео с пересечением 1М пока нет.</p>}
      {!denied && summary?.crossings > 0 && <form data-bonus-link-form className="oc-lead-card__bonus-linkform" onSubmit={(event) => { event.preventDefault(); rejectByLinks(); }}>
        <label className="oc-lead-card__form-field">
          <span className="oc-lead-card__form-label">Ссылка на ролик</span>
          <textarea className="oc-lead-card__textarea" rows={2} placeholder="Вставьте ссылку на ролик, по одной в строке" value={linkText} onChange={(event) => setLinkText(event.target.value)} />
        </label>
        <div className="oc-lead-card__bonus-linkform-row">
          <label className="oc-lead-card__form-field">
            <span className="oc-lead-card__form-label">Причина</span>
            <input className="oc-lead-card__input" placeholder="Почему бонус отклонён" value={linkReason} maxLength={500} onChange={(event) => setLinkReason(event.target.value)} />
          </label>
          <Button type="submit" size="small" disabled={disabled || linkBusy || !linkText.trim() || !linkReason.trim()}>Отклонить по ссылке</Button>
        </div>
        {linkResults && <ul className="oc-lead-card__bonus-results">{linkResults.map((r, i) => <li key={i} className={r.text === 'отклонён' ? 'oc-lead-card__bonus-result--ok' : 'oc-lead-card__bonus-result--bad'}>{r.link} — {r.text}</li>)}</ul>}
      </form>}
      {!denied && groups.length > 0 && <table className="oc-lead-card__bonus-table">
        <thead>
          <tr>
            <th>видео</th>
            <th className="oc-lead-card__bonus-col-num">просмотры</th>
            <th>публикация → 1М</th>
            <th aria-label="оплата" />
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.month}>
            <tr className="oc-lead-card__bonus-month">
              <td colSpan={4}>{g.label}</td>
            </tr>
            {g.bonuses.map((b) => (
              <Fragment key={b.id}>
              <tr data-bonus-id={b.id} className="oc-lead-card__bonus-row">
                <td>
                  <div className="oc-lead-card__bonus-video">
                  <a href={b.postUrl} target="_blank" rel="noopener noreferrer" className="oc-lead-card__bonus-link">
                    <Icon name="film-slate" size="sm" /> видео
                  </a>
                  {b.handle && <span className="oc-lead-card__bonus-account">
                    <span className={`oc-metric-table__platform oc-metric-table__platform--${b.platform}`}>{PLATFORM_LABEL[b.platform] ?? b.platform}</span> @{b.handle}
                  </span>}
                  </div>
                </td>
                <td className="oc-lead-card__bonus-col-num"><b>{fmtViewsCompact(b.postViews)}</b></td>
                <td className="oc-lead-card__bonus-dates">
                  {fmtDay(b.publishedAt)}
                  <span className="oc-lead-card__bonus-arrow">→</span>
                  <b>{fmtDay(b.reachedMillionAt)}</b>
                </td>
                <td className="oc-lead-card__bonus-col-pay">
                  <div className="oc-lead-card__bonus-actions">
                  {b.paid ? <Badge color="green" type="contained">оплачено</Badge> : b.rejectedAt ? <Badge color="red" type="outlined" className="oc-lead-card__bonus-status--wrap">отклонён · {fmtDay(b.rejectedAt)} · {b.rejectionReason}</Badge> : null}
                  {(b.actions?.pay || b.actions?.unpay) && <Button
                    hierarchy="secondary"
                    size="small"
                    className={b.paid ? 'oc-lead-card__bonus-pill--paid' : undefined}
                    leadingIcon={b.paid ? <Icon name="check" size="sm" /> : undefined}
                    disabled={disabled}
                    title={b.paid ? 'Нажмите, чтобы снять отметку оплаты' : 'Отметить бонус оплаченным'}
                    onClick={() => mutation.mutate({ id: b.id, paid: !b.paid })}
                  >
                    {b.paid ? 'Снять оплату' : 'Оплатить'}
                  </Button>}
                  {(b.actions?.reject || b.actions?.restore) && <Button hierarchy={b.rejectedAt ? 'secondary' : 'tertiary'} size="small" className="oc-lead-card__bonus-btn28" disabled={disabled} onClick={() => { setDecision({ id: b.id, rejected: !b.rejectedAt }); setReason(''); mutation.reset(); }}>{b.rejectedAt ? 'Восстановить' : 'Отклонить'}</Button>}
                  </div>
                </td>
              </tr>
              {decision?.id === b.id && <tr><td colSpan={4}>
                <form onSubmit={(event) => { event.preventDefault(); mutation.mutate(decision); }}>
                  <label>Причина решения по бонусу <input value={reason} maxLength={500} required onChange={(event) => setReason(event.target.value)} /></label>
                  <Button type="submit" size="small" disabled={disabled || !reason.trim()}>Подтвердить</Button>
                  <Button type="button" size="small" disabled={mutation.isPending} onClick={() => { setDecision(null); setReason(''); mutation.reset(); }}>Отмена</Button>
                </form>
              </td></tr>}
              </Fragment>
            ))}
          </tbody>
        ))}
      </table>}
      </details>
    </Card>
  );
}
